/**
 * userCacheService.js - Serviço de Cache de Usuário Centralizado
 * 
 * Elimina chamadas redundantes a supabase.auth.getUser() 
 * mantendo o usuário em cache por 5 minutos.
 * 
 * IMPORTANTE: Usa promise deduplication para evitar chamadas duplicadas
 * mesmo quando múltiplas funções chamam getCachedUser() em paralelo.
 * 
 * Economia estimada: ~300-600ms por tela que usa múltiplas funções de banco.
 */

import { supabase } from '../lib/supabase';

// Cache em memória
let cachedUser = null;
let cacheTimestamp = 0;

// Promise em andamento (para deduplicação)
let pendingPromise = null;

// Duração do cache: 5 minutos
const CACHE_DURATION_MS = 5 * 60 * 1000;

/**
 * Obtém o usuário autenticado do cache ou do Supabase.
 * Reutiliza o cache se ainda estiver válido (< 5 min).
 * 
 * IMPORTANTE: Usa deduplicação de promises para garantir que apenas
 * UMA chamada seja feita ao Supabase mesmo quando múltiplas funções
 * chamam getCachedUser() em paralelo.
 * 
 * @returns {Promise<Object|null>} Objeto do usuário ou null se não autenticado
 */
export const getCachedUser = async () => {
    const now = Date.now();

    // Retorna do cache se ainda válido
    if (cachedUser && (now - cacheTimestamp) < CACHE_DURATION_MS) {
        return cachedUser;
    }

    // Se já há uma chamada em andamento, espera ela terminar
    if (pendingPromise) {
        return pendingPromise;
    }

    // Cria nova promise e guarda referência para deduplicação
    pendingPromise = (async () => {
        try {
            const { data: { user }, error } = await supabase.auth.getUser();

            if (error) {
                return null;
            }

            cachedUser = user;
            cacheTimestamp = Date.now();

            return user;
        } catch (error) {
            return null;
        } finally {
            // Limpa a promise pendente após completar
            pendingPromise = null;
        }
    })();

    return pendingPromise;
};

/**
 * Obtém apenas o ID do usuário em cache.
 * Útil para funções que só precisam do ID.
 * 
 * @returns {Promise<string|null>} ID do usuário ou null
 */
export const getCachedUserId = async () => {
    const user = await getCachedUser();
    return user?.id || null;
};

/**
 * Invalida o cache do usuário.
 * Deve ser chamado no logout ou quando o usuário muda.
 */
export const invalidateUserCache = () => {
    cachedUser = null;
    cacheTimestamp = 0;
};

/**
 * Atualiza o cache com um novo usuário.
 * Útil após login para evitar chamada extra.
 * 
 * @param {Object} user - Objeto do usuário
 */
export const setUserCache = (user) => {
    cachedUser = user;
    cacheTimestamp = Date.now();
};

/**
 * Verifica se há um usuário em cache válido.
 * Não faz chamada de rede.
 * 
 * @returns {boolean} True se há cache válido
 */
export const hasValidCache = () => {
    return cachedUser && (Date.now() - cacheTimestamp) < CACHE_DURATION_MS;
};

/**
 * Obtém informações de debug do cache.
 * 
 * @returns {Object} Informações do cache
 */
export const getCacheInfo = () => {
    const now = Date.now();
    const age = cachedUser ? (now - cacheTimestamp) : 0;
    const remaining = cachedUser ? Math.max(0, CACHE_DURATION_MS - age) : 0;

    return {
        hasCache: !!cachedUser,
        userId: cachedUser?.id || null,
        ageMs: age,
        remainingMs: remaining,
        expiresIn: remaining > 0 ? `${Math.round(remaining / 1000)}s` : 'expired',
    };
};

export default {
    getCachedUser,
    getCachedUserId,
    invalidateUserCache,
    setUserCache,
    hasValidCache,
    getCacheInfo,
};
